import { signInWithEmailAndPassword, signOut, onAuthStateChanged, signInWithPopup, signInWithRedirect, getRedirectResult, signInWithCredential, GoogleAuthProvider, User as FirebaseUser, MultiFactorError } from 'firebase/auth';
import { auth, googleProvider } from '../config/firebase';
import { apiService } from './api';
import { User, LoginCredentials, RegisterData } from '../types';
import { isApprovedWorkspaceEmail } from '../utils/authUtils';

/** Decode Google ID token JWT payload (no verification; Firebase will verify on sign-in). */
function decodeGoogleIdToken(idToken: string): { email?: string } | null {
  try {
    const parts = idToken.split('.');
    if (parts.length !== 3) return null;
    const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const decoded = JSON.parse(atob(payload));
    return decoded;
  } catch {
    return null;
  }
}

export const firebaseAuthService = {
  async login(credentials: LoginCredentials): Promise<{ user: User; token: string } | { requiresMfa: true; error: MultiFactorError }> {
    try {
      // Sign in with Firebase Auth
      const userCredential = await signInWithEmailAndPassword(auth, credentials.email, credentials.password);
      const firebaseUser = userCredential.user;

      // Get the Firebase ID token
      const token = await firebaseUser.getIdToken();

      // Get user data from our backend using the token
      const response = await apiService.get<{ user: User }>('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.user) {
        return { user: response.user, token };
      }
      throw new Error('Failed to get user data');
    } catch (error: any) {
      // Check if MFA is required
      if (error.code === 'auth/multi-factor-auth-required') {
        return { requiresMfa: true, error: error as MultiFactorError };
      }

      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        throw new Error('Invalid credentials');
      } else if (error.code === 'auth/too-many-requests') {
        throw new Error('Too many failed attempts. Please try again later.');
      } else if (error.code === 'auth/user-disabled') {
        throw new Error('Account has been disabled');
      }
      throw new Error(error.message || 'Login failed');
    }
  },

  /**
   * Complete login after MFA verification
   * @param mfaToken Firebase ID token obtained after MFA verification
   */
  async completeLoginWithMfa(mfaToken: string): Promise<{ user: User; token: string }> {
    try {
      // Get user data from our backend using the MFA token
      const response = await apiService.get<{ user: User }>('/auth/me', {
        headers: {
          Authorization: `Bearer ${mfaToken}`
        }
      });

      if (response.user) {
        return { user: response.user, token: mfaToken };
      }
      throw new Error('Failed to get user data');
    } catch (error: any) {
      throw new Error(error.message || 'Failed to complete login');
    }
  },

  async register(data: RegisterData): Promise<{ user: User; token: string }> {
    try {
      // Register with backend first (creates Firebase Auth user + local record atomically).
      const response = await apiService.post<{ message: string; user: User; firebaseUid: string }>('/auth/register', {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        companyId: data.companyId,
        invitationToken: data.invitationToken,
      });

      if (response.user) {
        // Sign in with the newly created credentials to get an ID token.
        const userCredential = await signInWithEmailAndPassword(auth, data.email, data.password);
        const firebaseUser = userCredential.user;
        const token = await firebaseUser.getIdToken();

        // Load canonical user shape from backend /auth/me.
        try {
          const me = await apiService.get<{ user: User }>('/auth/me', {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });
          return { user: me.user || response.user, token };
        } catch {
          // Fallback to the register payload if /auth/me is temporarily unavailable.
          return { user: response.user, token };
        }
      }
      throw new Error('Failed to register user');
    } catch (error: any) {
      if (error?.code === 'auth/weak-password') {
        throw new Error('Password is too weak');
      } else if (error?.code === 'auth/invalid-email') {
        throw new Error('Invalid email address');
      } else if (error?.response?.data?.error?.message) {
        throw new Error(error.response.data.error.message);
      }
      throw new Error(error.message || 'Registration failed');
    }
  },

  async getCurrentUser(token: string): Promise<{ user: User; freshToken?: string }> {
    try {
      const response = await apiService.get<{ user: User; claimsSynced?: boolean }>('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      if (response.user) {
        let freshToken: string | undefined;
        if (response.claimsSynced) {
          const currentUser = auth.currentUser;
          if (currentUser) {
            console.log('🔄 Custom claims synced on backend, force-refreshing Firebase ID token...');
            freshToken = await currentUser.getIdToken(true);
            localStorage.setItem('token', freshToken);
          }
        }
        return { user: response.user, freshToken };
      }
      throw new Error('Failed to get current user');
    } catch (error) {
      throw error;
    }
  },

  async logout(): Promise<void> {
    try {
      await signOut(auth);
    } catch (error) {
      console.error('Firebase logout error:', error);
    }
  },

  // Listen to auth state changes
  onAuthStateChanged(callback: (user: FirebaseUser | null) => void) {
    return onAuthStateChanged(auth, callback);
  },

  // Get current Firebase user
  getCurrentFirebaseUser(): FirebaseUser | null {
    return auth.currentUser;
  },

  // Get Firebase ID token
  async getIdToken(): Promise<string | null> {
    const user = auth.currentUser;
    if (user) {
      return await user.getIdToken();
    }
    return null;
  },

  /**
   * Sign in with a Google ID token (e.g. from Google One Tap).
   * Only allows emails from approved workspace domains (VITE_INTERNAL_DOMAINS).
   */
  async loginWithGoogleIdToken(idToken: string): Promise<{ user: User; token: string }> {
    try {
      const payload = decodeGoogleIdToken(idToken);
      const email = payload?.email;
      if (!email || !isApprovedWorkspaceEmail(email)) {
        const domain = email ? email.split('@')[1] : 'unknown';
        throw new Error(`Sign-in with ${domain} is not allowed. Use an approved workspace account.`);
      }

      const credential = GoogleAuthProvider.credential(idToken);
      const userCredential = await signInWithCredential(auth, credential);
      const firebaseUser = userCredential.user;

      const token = await firebaseUser.getIdToken();
      const response = await apiService.get<{ user: User }>('/auth/me', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.user) {
        return { user: response.user, token };
      }
      throw new Error('Failed to get user data');
    } catch (error: any) {
      if (error.message?.includes('not allowed')) throw error;
      throw new Error(error.message || 'Google sign-in failed');
    }
  },

  // Sign in with Google (for Google Workspace users)
  async loginWithGoogle(): Promise<{ user: User; token: string }> {
    try {
      // Sign in with Google provider
      const userCredential = await signInWithPopup(auth, googleProvider);
      const firebaseUser = userCredential.user;

      // Get the Firebase ID token
      const token = await firebaseUser.getIdToken();

      // Get user data from our backend using the token
      const response = await apiService.get<{ user: User }>('/auth/me', {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.user) {
        return { user: response.user, token };
      }
      throw new Error('Failed to get user data');
    } catch (error: any) {
      if (error.code === 'auth/popup-closed-by-user') {
        throw new Error('Sign-in cancelled');
      } else if (error.code === 'auth/popup-blocked') {
        throw new Error('Popup blocked. Please allow popups for this site.');
      } else if (error.code === 'auth/account-exists-with-different-credential') {
        throw new Error('An account already exists with this email. Please sign in with your password.');
      }
      throw new Error(error.message || 'Google sign-in failed');
    }
  },

  // Automatic sign-in with Google Workspace (for /internal route)
  // Uses redirect flow for seamless SSO experience
  async loginWithGoogleRedirect(): Promise<void> {
    try {
      // Use redirect instead of popup for better SSO experience
      await signInWithRedirect(auth, googleProvider);
    } catch (error: any) {
      console.error('Redirect sign-in error:', error);
      throw new Error(error.message || 'Google sign-in redirect failed');
    }
  },

  // Check for redirect result (call this on page load for /internal route)
  async getRedirectResult(): Promise<{ user: User; token: string } | null> {
    try {
      const result = await getRedirectResult(auth);
      if (result) {
        const firebaseUser = result.user;
        const token = await firebaseUser.getIdToken();

        // Get user data from our backend using the token
        const response = await apiService.get<{ user: User }>('/auth/me', {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });

        if (response.user) {
          return { user: response.user, token };
        }
      }
      return null;
    } catch (error: any) {
      console.error('Get redirect result error:', error);
      throw new Error(error.message || 'Failed to get redirect result');
    }
  },

  /**
   * Masquerade as another user (Super Admin only)
   * Returns generic tokens that must be used with signInWithCustomToken
   */
  async masquerade(targetUserId: number): Promise<{ targetToken: string; adminToken: string }> {
    try {
      const response = await apiService.post<{ success: boolean; targetToken: string; adminToken: string }>('/auth/masquerade', {
        targetUserId
      });

      if (response.success && response.targetToken && response.adminToken) {
        return {
          targetToken: response.targetToken,
          adminToken: response.adminToken
        };
      }
      throw new Error('Invalid response from masquerade endpoint');
    } catch (error: any) {
      throw new Error(error.message || 'Masquerade failed');
    }
  },

  /**
   * Sign in with a custom token (used for masquerading)
   */
  async signInWithCustomToken(token: string): Promise<{ user: User; token: string }> {
    try {
      // Import dynamically to avoid circular dependencies if any, or just use the global import
      const { signInWithCustomToken } = await import('firebase/auth');
      const userCredential = await signInWithCustomToken(auth, token);
      const firebaseUser = userCredential.user;

      // Get the fresh ID token
      const idToken = await firebaseUser.getIdToken();

      // Get user data from our backend
      const response = await apiService.get<{ user: User }>('/auth/me', {
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      });

      if (response.user) {
        return { user: response.user, token: idToken };
      }
      throw new Error('Failed to get user data');
    } catch (error: any) {
      throw new Error(error.message || 'Sign in with custom token failed');
    }
  }
};
